import React, { useState } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFileOutlined";
import DownloadIcon from "@mui/icons-material/DownloadOutlined";
import SyncIcon from "@mui/icons-material/SyncOutlined";
import apiFetch from "../../utils/apiFetch";
import { useSnackAlert } from "../../contexts/SnackAlertContext";

// OneRoster import/export and DataRollup sync controls for the nav drawer.
// Gating mirrors the legacy LeftDrawer / TopNavBar: the OneRoster section needs
// the OneRoster (manage) permission and the school's one_roster_export flag, and
// the Upload (SFTP push) button is additionally limited to Florida conference
// schools. Sync DataRollup needs the DataRollup Sync (manage) permission. All of
// those values arrive in appProps, so no extra request is made here.
export default function RosterActions({ appProps, schoolSlug }) {
  const { setSnackAlert } = useSnackAlert();
  const [syncInitiated, setSyncInitiated] = useState(false);

  const school = appProps?.school;
  const permissions = appProps?.permissions ?? {};
  const schoolRef = school?.slug ?? school?.id;

  const canManageOneRoster = Boolean(
    permissions.one_roster && school?.one_roster_export
  );
  const canUploadOneRoster =
    canManageOneRoster && Boolean(school?.is_florida_conference);
  const canSyncDataRollup = Boolean(permissions.datarollup_sync);

  const postAction = async (path, body) => {
    try {
      const response = await apiFetch(path, {
        method: "POST",
        schoolSlug,
        body: JSON.stringify(body),
      });
      const payload = await response.json().catch(() => ({}));
      if (response.ok) {
        setSnackAlert(payload.message || "Request started.", "success");
        return true;
      }
      setSnackAlert(payload.message || "Something went wrong.", "error");
      return false;
    } catch (e) {
      setSnackAlert("Something went wrong.", "error");
      return false;
    }
  };

  const handleDownload = () =>
    postAction("/api/reports/download_one_roster", { school_id: schoolRef });

  const handleUpload = () =>
    postAction("/api/reports/upload_one_roster", { school_id: schoolRef });

  const handleSync = async () => {
    if (syncInitiated) return;
    const ok = await postAction("/api/schools/datarollup_sync", {
      id: schoolRef,
    });
    if (ok) setSyncInitiated(true);
  };

  if (!canManageOneRoster && !canSyncDataRollup) return null;

  return (
    <>
      {canManageOneRoster && (
        <Box>
          <Typography
            sx={{ fontSize: 13, fontWeight: 600, color: "#00000099", mb: 0.5 }}
          >
            OneRoster
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            {canUploadOneRoster && (
              <Tooltip title="Upload OneRoster files" arrow>
                <IconButton
                  aria-label="Upload OneRoster files"
                  onClick={handleUpload}
                >
                  <UploadFileIcon sx={{ color: "#00000080" }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Download OneRoster files" arrow>
              <IconButton
                aria-label="Download OneRoster files"
                onClick={handleDownload}
              >
                <DownloadIcon sx={{ color: "#00000080" }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      {canSyncDataRollup && (
        <Box
          role="button"
          aria-label="Sync DataRollup"
          aria-disabled={syncInitiated}
          onClick={handleSync}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            py: 1,
            cursor: syncInitiated ? "default" : "pointer",
            opacity: syncInitiated ? 0.5 : 1,
            borderRadius: 1,
            "&:hover": {
              backgroundColor: syncInitiated ? "transparent" : "#00000008",
            },
          }}
        >
          <SyncIcon sx={{ color: "#00000080" }} />
          <Typography sx={{ fontSize: 15, color: "#000000" }}>
            {syncInitiated ? "DataRollup sync initiated" : "Sync DataRollup"}
          </Typography>
        </Box>
      )}
    </>
  );
}
